import { useEffect, type ReactNode } from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
};

/**
 * Full-screen focused overlay for a dashboard card. Triggered by the ↗ expand
 * button in a card header. Closes on backdrop click, the × button, or Escape.
 */
export function CardModal({ open, onClose, title, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm animate-[fadeUp_0.2s_ease]" />

      {/* Panel */}
      <div
        className="relative w-full max-w-5xl h-[82vh] bg-d_card border border-d_border/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-[fadeUp_0.25s_cubic-bezier(0.16,1,0.3,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-d_border/20 flex-shrink-0">
          <h2 className="font-serif text-[20px] font-semibold tracking-[0.01em] text-d_text">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md flex items-center justify-center text-d_text2 hover:text-d_text border border-d_border/30 hover:border-d_accent/50 bg-d_card2/60 hover:bg-d_card2 transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * The ↗ button that lives in a card header. Clicking it expands the card.
 * Kept visually identical to the old decorative span, but now functional.
 */
export function ExpandButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Expand"
      title="Expand"
      className="w-[26px] h-[26px] rounded-full bg-d_celeste/[.04] border border-d_border/20 hover:border-d_accent/40 flex items-center justify-center text-[11px] text-d_text2 hover:text-d_accent cursor-pointer transition-colors flex-shrink-0"
    >
      ↗
    </button>
  );
}
